import React, { useEffect, useRef, useState } from "react";
import './contoller.css';
import { useQuery, useSubscription } from '@apollo/client';
import { COMMAND_SUBSCRIPTION, INITIAL_SUBSCRIPTION, RESET_RENDERER_CONNECTION } from '../../graphql/subscription';
import { GET_COMMANDS, GET_INITIAL_COMMANDS } from '../../graphql/queries';

const baseCaptionsApiUrl = process.env.REACT_APP_CAPTION_API;

const ControllerPage = () => {
  const params = new URLSearchParams(window.location.search);
  const renderer = params.get('renderer');
  const debug = params.get('debug');

  const [isLoaded, setIsLoaded] = useState(false);
  const [scenePath, setScenePath] = useState(null);
  const [captions, setCaptions] = useState(null);
  const [captionData, setCaptionData] = useState({
    renderer,
    caption: { action: 'clear', id: "" },
    updatedBy: 'none'
  });
  const [initialCommadData, setInitialCommadData] = useState({
    eventId: '',
    renderer
  });

  const sceneRef = useRef(null);
  const messageQueue = useRef([]);

  // Queries
  const lastCommand = useQuery(GET_COMMANDS, {
    variables: { renderer },
    fetchPolicy: "cache-first",
    skip: !!captionData.caption.data,
  });

  const initialCommand = useQuery(GET_INITIAL_COMMANDS, {
    variables: { renderer },
    fetchPolicy: "cache-first",
    skip: !!initialCommadData.data,
  });

  // Subscriptions
  const commandSubscription = useSubscription(COMMAND_SUBSCRIPTION, {
    variables: { renderer }
  });

  const initialSubscription = useSubscription(INITIAL_SUBSCRIPTION, {
    variables: { renderer }
  });

  const resetRendererConnection = useSubscription(RESET_RENDERER_CONNECTION, {
    variables: { rendererId: renderer }
  });

  // Refetch GET_COMMANDS when the component is loaded
  useEffect(() => {
    if (isLoaded && lastCommand.refetch) {
      lastCommand.refetch().then(({ data }) => {
        if (data?.getCommands) {
          data.getCommands.forEach(commandData => {
            setCaptionData(prev => ({
              ...prev,
              ...commandData,
              updatedBy: 'refetch'
            }));
            sendDataToScene({ data: commandData, type: 'command', updatedBy: 'refetch' });
          });
        }
      }).catch(err => {
        console.error("Refetching GET_COMMANDS failed:", err);
      });
    }
  }, [isLoaded, lastCommand.refetch]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'modules-loaded') {
        console.log('Modules loaded message received from iframe.');
        setIsLoaded(true);

        // Send all queued messages EXCEPT commands
        const remainingQueue = [];

        while (messageQueue.current.length > 0) {
          const msg = messageQueue.current.shift();
          if (msg.type === 'command') {
            // Skip sending, we'll refetch instead
            console.log('⏭️ Skipping old command message, will refetch.');
          } else {
            sceneRef.current?.contentWindow.postMessage(msg, '*');
          }
        }

        // Refetch latest commands
        if (lastCommand.refetch) {
          lastCommand.refetch().then(({ data }) => {
            if (data?.getCommands) {
              data.getCommands.forEach(commandData => {
                const msg = {
                  data: commandData,
                  type: 'command',
                  updatedBy: 'refetch'
                };
                sceneRef.current?.contentWindow.postMessage(msg, '*');
              });
            }
          }).catch(err => {
            console.error("Refetching GET_COMMANDS failed:", err);
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [lastCommand.refetch]);


  // Fetch captions
  useEffect(() => {
    const fetchCaptions = async () => {
      try {
        const response = await fetch(`${baseCaptionsApiUrl}/${renderer}/details`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        setCaptions(data);
      } catch (error) {
        console.error('Error fetching captions:', error);
      }
    };
    fetchCaptions();
  }, [renderer, initialCommadData.data, isLoaded]);

  // Command subscription
  useEffect(() => {
    const data = commandSubscription.data?.subscriptionToCommand;
    if (data?.caption?.data) {
      setCaptionData(prev => ({
        ...prev,
        ...data,
        updatedBy: 'subscription'
      }));
      sendDataToScene({ data, type: 'command', updatedBy: 'subscription' });
    }
  }, [commandSubscription]);

  // Last commands (query)
  useEffect(() => {
    if (!isLoaded && lastCommand.data?.getCommands) {
      const commandData = lastCommand.data.getCommands;
      setCaptionData(prev => ({
        ...prev,
        ...commandData[0],
        updatedBy: 'query'
      }));
      commandData.forEach(data => {
        sendDataToScene({ data, type: 'command', updatedBy: 'query' });
      });
    }
  }, [lastCommand, isLoaded]);

  // Initial subscription
  useEffect(() => {
    const newInitialData = initialSubscription.data?.subscriptionToInitialCommand;
    if (newInitialData) {
      setInitialCommadData(prev => ({
        ...prev,
        ...newInitialData
      }));

      const newScenePath = newInitialData.scenePath || `/scenes/${newInitialData.eventId}`;
      if (newScenePath !== scenePath) {
        setIsLoaded(false);
        setScenePath(newScenePath);
      }

      sendDataToScene({ data: newInitialData, type: 'initial', updatedBy: 'subscription' });
    }
  }, [initialSubscription, scenePath]);

  // Initial commands (query)
  useEffect(() => {
    const newInitialData = initialCommand.data?.getInitialCommands;
    if (newInitialData) {
      setInitialCommadData(prev => ({
        ...prev,
        ...newInitialData
      }));

      const newScenePath = newInitialData.scenePath || `/scenes/${newInitialData.eventId}`;
      if (newScenePath !== scenePath) {
        setIsLoaded(false);
        setScenePath(newScenePath);
      }

      sendDataToScene({ data: newInitialData, type: 'initial', updatedBy: 'query' });
    }
  }, [initialCommand, scenePath, isLoaded]);

  // Reset renderer connection
  useEffect(() => {
    const resetData = resetRendererConnection.data?.resetRendererConnection;
    if (resetData?.reset) {
      console.log(`Renderer ${resetData.rendererId} connection reset.`);

      // Reload iframe
      if (sceneRef.current) {
        sceneRef.current.src = scenePath; // Reload the current scene
      }
      setIsLoaded(false); // Reset loaded state
    }
  }, [resetRendererConnection]);

  // Captions to scene
  useEffect(() => {
    if (!captions) return;
    sendDataToScene({ data: captions, type: 'caption', updatedBy: 'query' });
  }, [captions, isLoaded]);

  // Safely send data to iframe (with queueing)
  const sendDataToScene = (message) => {
    if (sceneRef.current && isLoaded) {
      sceneRef.current.contentWindow.postMessage(message, '*');
    } else {
      // Deduplicate by message.type: keep only the latest per type
      const existingIndex = messageQueue.current.findIndex(m => m.type === message.type);
      if (existingIndex !== -1) {
        messageQueue.current[existingIndex] = message; // Replace with the latest
      } else {
        messageQueue.current.push(message); // Add new
      }
    }
  };

  // Flush queue on iframe load
  const handleIframeLoad = () => {
  // Send only 'caption' messages first
  const remainingQueue = [];

  for (const msg of messageQueue.current) {
    if (msg.type === 'caption') {
      sceneRef.current?.contentWindow.postMessage(msg, '*');
    } else {
      remainingQueue.push(msg); // Keep others
    }
  }

  messageQueue.current = remainingQueue;
};

  return (
    <div
      style={{
        position: 'absolute',
        width: '100vw',
        height: '56.25vw', // TODO: Aspect ratio should be dynamic and coming from broken
        maxWidth: '177.78vh',
        maxHeight: '100vh',
        backgroundImage: debug === 'true' ? `url(/images/bg.png)` : '',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        margin: 'auto',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      }}
    >
      {scenePath && (
        <iframe
          ref={sceneRef}
          src={scenePath}
          title="Layer"
          style={{
            width: "100%",
            height: "100%",
            position: 'absolute',
            top: '0',
            left: '0',
            border: 'none'
          }}
          onLoad={handleIframeLoad}
        />
      )}
    </div>
  );
};

export default ControllerPage;